import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Tag, Button, Input, Select, Space, Typography, Card,
  Popconfirm, Modal, message, Tooltip, Badge,
} from 'antd';
import {
  SearchOutlined, StopOutlined, CheckCircleOutlined,
  CrownOutlined, EyeOutlined, DeleteOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { theme as antdTheme } from 'antd';
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const AdminOrgs = () => {
  const { token: tk }   = antdTheme.useToken();
  const navigate        = useNavigate();
  const [orgs, setOrgs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [planFilter, setPlan]   = useState('');
  const [statusFilter, setStatus] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [planModal, setPlanModal]   = useState(null); // org to change plan for
  const [newPlan, setNewPlan]       = useState('pro');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search)       params.search = search;
      if (planFilter)   params.plan   = planFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get('/api/admin/orgs', { headers: authHeader(), params });
      setOrgs(res.data);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to load organisations');
    } finally {
      setLoading(false);
    }
  }, [search, planFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setLoaderFor = (id, val) => setActionLoading(p => ({ ...p, [id]: val }));

  const handleSuspend = async (org) => {
    setLoaderFor(org.id, 'suspend');
    try {
      await axios.put(`/api/admin/orgs/${org.id}/suspend`, {}, { headers: authHeader() });
      message.success(`"${org.name}" suspended`);
      load();
    } catch { message.error('Failed to suspend'); }
    finally { setLoaderFor(org.id, null); }
  };

  const handleUnsuspend = async (org) => {
    setLoaderFor(org.id, 'unsuspend');
    try {
      await axios.put(`/api/admin/orgs/${org.id}/unsuspend`, {}, { headers: authHeader() });
      message.success(`"${org.name}" unsuspended`);
      load();
    } catch { message.error('Failed to unsuspend'); }
    finally { setLoaderFor(org.id, null); }
  };

  const handleDelete = async (org) => {
    setLoaderFor(org.id, 'delete');
    try {
      await axios.delete(`/api/admin/orgs/${org.id}`, { headers: authHeader() });
      message.success(`"${org.name}" deleted`);
      load();
    } catch { message.error('Failed to delete'); }
    finally { setLoaderFor(org.id, null); }
  };

  const handleOverridePlan = async () => {
    if (!planModal) return;
    setLoaderFor(planModal.id, 'plan');
    try {
      await axios.put(`/api/admin/orgs/${planModal.id}/plan`, { plan: newPlan }, { headers: authHeader() });
      message.success(`Plan set to ${newPlan} for "${planModal.name}"`);
      setPlanModal(null);
      load();
    } catch { message.error('Failed to change plan'); }
    finally { setLoaderFor(planModal.id, null); }
  };

  const columns = [
    {
      title: 'Organisation',
      dataIndex: 'name',
      key: 'name',
      render: (name, row) => (
        <div>
          <div
            style={{ fontWeight: 600, cursor: 'pointer', color: tk.colorPrimary }}
            onClick={() => navigate(`/admin/orgs/${row.id}`)}
          >
            {name}
          </div>
          <div style={{ fontSize: 12, color: tk.colorTextSecondary }}>{row.owner_email}</div>
        </div>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      width: 100,
      render: (plan, row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Tag
            icon={plan === 'pro' ? <CrownOutlined /> : null}
            color={plan === 'pro' ? 'gold' : 'default'}
            style={{ width: 'fit-content' }}
          >
            {plan?.toUpperCase()}
          </Tag>
          {row.plan_status === 'past_due' && <Tag color="orange" style={{ width: 'fit-content', fontSize: 11 }}>Past Due</Tag>}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_, row) => {
        if (row.suspended) return <Badge status="error"   text="Suspended" />;
        if (!row.ses_verified) return <Badge status="warning" text="No SES" />;
        return <Badge status="success" text="Active" />;
      },
    },
    {
      title: 'Campaigns / mo',
      dataIndex: 'campaigns_this_month',
      key: 'campaigns',
      width: 120,
      align: 'center',
      render: v => <Text style={{ fontWeight: 600 }}>{v}</Text>,
    },
    {
      title: 'Users',
      dataIndex: 'user_count',
      key: 'user_count',
      width: 80,
      align: 'center',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: v => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, row) => (
        <Space size={4}>
          <Tooltip title="View details">
            <Button
              size="small" icon={<EyeOutlined />}
              onClick={() => navigate(`/admin/orgs/${row.id}`)}
            />
          </Tooltip>

          {row.suspended ? (
            <Tooltip title="Unsuspend">
              <Button
                size="small" icon={<CheckCircleOutlined />} type="primary"
                loading={actionLoading[row.id] === 'unsuspend'}
                onClick={() => handleUnsuspend(row)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Suspend">
              <Popconfirm
                title={`Suspend "${row.name}"?`}
                description="Their sends will be blocked immediately."
                onConfirm={() => handleSuspend(row)}
                okText="Suspend" okButtonProps={{ danger: true }}
              >
                <Button
                  size="small" icon={<StopOutlined />} danger
                  loading={actionLoading[row.id] === 'suspend'}
                />
              </Popconfirm>
            </Tooltip>
          )}

          <Tooltip title="Override plan">
            <Button
              size="small" icon={<CrownOutlined />}
              onClick={() => { setPlanModal(row); setNewPlan(row.plan === 'pro' ? 'free' : 'pro'); }}
              loading={actionLoading[row.id] === 'plan'}
            />
          </Tooltip>

          <Tooltip title="Delete org">
            <Popconfirm
              title={`Delete "${row.name}"?`}
              description="This permanently deletes all campaigns, subscribers, and data."
              onConfirm={() => handleDelete(row)}
              okText="Delete" okButtonProps={{ danger: true }}
            >
              <Button
                size="small" icon={<DeleteOutlined />} danger
                loading={actionLoading[row.id] === 'delete'}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>All Organisations</Title>
          <Text style={{ color: tk.colorTextSecondary }}>{orgs.length} organisations</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 16, borderRadius: 10, border: `1px solid ${tk.colorBorderSecondary}` }}>
        <Space wrap>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 260 }}
            allowClear
          />
          <Select
            value={planFilter || 'all'}
            onChange={v => setPlan(v === 'all' ? '' : v)}
            style={{ width: 120 }}
          >
            <Option value="all">All Plans</Option>
            <Option value="free">Free</Option>
            <Option value="pro">Pro</Option>
          </Select>
          <Select
            value={statusFilter || 'all'}
            onChange={v => setStatus(v === 'all' ? '' : v)}
            style={{ width: 140 }}
          >
            <Option value="all">All Statuses</Option>
            <Option value="active">Active</Option>
            <Option value="suspended">Suspended</Option>
            <Option value="past_due">Past Due</Option>
          </Select>
        </Space>
      </Card>

      <Card style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }}>
        <Table
          dataSource={orgs}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: t => `${t} orgs` }}
          rowClassName={row => row.suspended ? 'ant-table-row-danger' : ''}
        scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* Plan override modal */}
      <Modal
        open={!!planModal}
        title={`Override Plan — ${planModal?.name}`}
        onCancel={() => setPlanModal(null)}
        onOk={handleOverridePlan}
        okText="Apply"
        confirmLoading={!!actionLoading[planModal?.id]}
      >
        <div style={{ padding: '12px 0' }}>
          <Text style={{ display: 'block', marginBottom: 12 }}>
            Current plan: <strong>{planModal?.plan?.toUpperCase()}</strong>
          </Text>
          <Select value={newPlan} onChange={setNewPlan} style={{ width: '100%' }}>
            <Option value="free">Free</Option>
            <Option value="pro">Pro (manual grant — no Stripe required)</Option>
          </Select>
          {newPlan === 'pro' && (
            <Text style={{ display: 'block', marginTop: 8, color: '#f59e0b', fontSize: 13 }}>
              This grants Pro access for 1 year without a Stripe subscription.
            </Text>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AdminOrgs;
